import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import {
  canCancel,
  canReschedule,
  isUpcomingConfirmed,
  useBookingCancellation,
} from '../../../../../hooks/booking/useBookingCancellation'

import Icon from '../../../../ui/Icon'
import { Button } from '../../../../buttons/Button'
import {
  DropdownMenu,
  DropdownMenuItem,
} from '../../../../dropdownMenu/DropdownMenu'
import { BookingFundingBadge } from '../../../history/BookingFundingBadge'

import { Booking } from '../../../../../types/booking/booking'
import { formatMinutes, timeOf } from '../../../../../utils/booking/booking'
import {
  displayDay,
  displayShortDayOfWeek,
  displayShortMonth,
} from '../../../../../utils/dateUtils'

type UpcomingRowProps = {
  item: Booking
}

export const UpcomingRow = ({ item }: UpcomingRowProps) => {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation(['dashboard', 'history'])
  const { confirmCancel } = useBookingCancellation('/dashboard')

  const courtTitle = item.clubName
    ? t('dashboard.upcoming-list.court-title', {
        court: item.courtName,
        club: item.clubName,
      })
    : item.courtName

  const scheduleLabel = t('dashboard.upcoming-list.scheduale', {
    day: displayShortDayOfWeek(item.startTime, i18n.language),
    time: timeOf(item.startTime),
    duration: formatMinutes(item.durationMinutes),
  })

  const rescheduleLabel = t(
    'history:history.booking-history-list.reschedules',
    {
      used: item.rescheduleCount,
      max: item.maxReschedules,
    }
  )

  const menuItems: DropdownMenuItem[] = isUpcomingConfirmed(item)
    ? [
        ...(canReschedule(item)
          ? [
              {
                key: 'reschedule',
                label: t('history:history.booking-history-list.reschedule-btn'),
                icon: 'calendar',
                onClick: () =>
                  navigate(`/history/reschedule/${item.id}`, {
                    state: { booking: item },
                  }),
              },
            ]
          : []),
        ...(canCancel(item)
          ? [
              {
                key: 'cancel',
                label: t('dashboard.upcoming-list.cancel-btn'),
                icon: 'x',
                danger: true,
                onClick: () => confirmCancel(item),
              },
            ]
          : []),
      ]
    : []

  const actions = isUpcomingConfirmed(item) ? (
    <div className="upcoming-row-actions ph-row gap-1">
      {menuItems.map(action => (
        <Button
          key={action.key}
          label={action.label}
          icon={<Icon name={action.icon!} size={13} />}
          variant="ghost"
          size="sm"
          onClick={action.onClick}
        />
      ))}
    </div>
  ) : null

  const menu =
    menuItems.length > 0 ? (
      <DropdownMenu
        label={t('history:history.booking-history-list.more-actions')}
        items={menuItems}
      />
    ) : undefined

  const funding = <BookingFundingBadge booking={item} />

  const dateBox = (
    <div className="date-box">
      <div className="month">
        {displayShortMonth(item.startTime, i18n.language)}
      </div>
      <div className="day">{displayDay(item.startTime)}</div>
    </div>
  )

  return (
    <>
      <div className="upcoming-row d-none d-md-grid">
        {dateBox}

        <div>
          <div className="title">{courtTitle}</div>
          <div className="meta-time">{scheduleLabel}</div>
          <div className="tiny muted">{rescheduleLabel}</div>
        </div>

        {funding}
        {actions}
      </div>

      <div className="upcoming-row-compact d-md-none">
        {dateBox}

        <div className="upcoming-row-compact-main">
          <div className="title" title={courtTitle}>
            {courtTitle}
          </div>
          <div className="meta-time">{scheduleLabel}</div>
        </div>

        <div className="upcoming-row-compact-corner">
          {funding}
          {menu}
        </div>
      </div>
    </>
  )
}
